import { useState, useEffect, useCallback } from 'react';
import { settingsService, UiFeatureFlags } from '../services/settingsService';

const FEATURE_FLAGS_CACHE_KEY = 'storyboard_feature_flags';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedFeatureFlags {
  data: UiFeatureFlags;
  timestamp: number;
}

// Default feature flags - SECURE BY DEFAULT
// All features are disabled until explicitly enabled by authenticated API
const DEFAULT_FEATURE_FLAGS: UiFeatureFlags = {
  dashboardShowReportCards: false,
  dashboardShowMattersList: false,
  medicalRecordsUploadEnabled: false
};

/**
 * Get cached feature flags from localStorage
 */
function getCachedFeatureFlags(): UiFeatureFlags | null {
  try {
    const cached = localStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
    if (!cached) return null;
    
    const parsedCache: CachedFeatureFlags = JSON.parse(cached);
    const isExpired = Date.now() - parsedCache.timestamp > CACHE_DURATION;
    
    if (isExpired) {
      localStorage.removeItem(FEATURE_FLAGS_CACHE_KEY);
      return null;
    }
    
    return parsedCache.data;
  } catch (error) {
    console.warn('Failed to parse cached feature flags:', error);
    localStorage.removeItem(FEATURE_FLAGS_CACHE_KEY);
    return null;
  }
}

/**
 * Cache feature flags in localStorage
 */
function setCachedFeatureFlags(flags: UiFeatureFlags): void {
  try {
    const cacheData: CachedFeatureFlags = {
      data: flags,
      timestamp: Date.now()
    };
    localStorage.setItem(FEATURE_FLAGS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache feature flags:', error);
  }
}

/**
 * Hook for accessing and managing UI feature flags
 */
export function useFeatureFlags() {
  // Initialize with cached data or defaults
  const [features, setFeatures] = useState<UiFeatureFlags | null>(() => {
    return getCachedFeatureFlags() || null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load feature flags with enhanced error handling
  const loadFeatures = useCallback(async (isRetry: boolean = false) => {
    try {
      if (!isRetry) {
        setIsLoading(true);
      }
      setError(null);
      
      console.log('Loading UI feature flags...');
      const flags = await settingsService.getUiFeatures();
      setFeatures(flags);
      
      // Cache the loaded flags
      setCachedFeatureFlags(flags);
      console.log('UI feature flags loaded successfully:', flags);
      
    } catch (err: any) {
      console.error('Failed to load feature flags:', err);
      
      // Check if this is an authentication error
      const isAuthError = (
        err.message?.includes('401') ||
        err.status === 401 ||
        err.message?.includes('Unauthorized')
      );
      
      if (isAuthError && !isRetry) {
        console.log('Authentication error loading feature flags, will retry after auth completes...');
        setError('Loading feature flags...');
        
        // Set up retry when authentication completes
        setTimeout(() => {
          console.log('Retrying feature flags load after authentication delay...');
          loadFeatures(true);
        }, 2000);
        
        // Use cached data or secure defaults while waiting for retry
        const cachedFlags = getCachedFeatureFlags();
        if (cachedFlags) {
          console.log('Using cached feature flags during auth retry');
          setFeatures(cachedFlags);
        } else {
          console.log('Using secure default feature flags during auth retry');
          setFeatures(DEFAULT_FEATURE_FLAGS);
        }
        
      } else {
        // Final failure - use cached or defaults
        setError('Failed to load feature flags');
        
        const cachedFlags = getCachedFeatureFlags();
        if (cachedFlags) {
          console.log('Using cached feature flags due to API error');
          setFeatures(cachedFlags);
        } else {
          console.log('Using secure default feature flags due to API error');
          setFeatures(DEFAULT_FEATURE_FLAGS);
        }
      }
    } finally {
      if (!isRetry) {
        setIsLoading(false);
      }
    }
  }, []);

  // Update feature flags
  const updateFeatures = useCallback(async (updates: Partial<UiFeatureFlags>) => {
    try {
      setError(null);
      const success = await settingsService.updateUiFeatures(updates);
      
      if (success) {
        // Reload features to get the latest state
        await loadFeatures();
        return true;
      } else {
        setError('Failed to update feature flags');
        return false;
      }
    } catch (err) {
      console.error('Failed to update feature flags:', err);
      setError('Failed to update feature flags');
      return false;
    }
  }, [loadFeatures]);

  // Load features on mount
  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  // TEMPORARILY DISABLED: Focus reload breaks drag/drop operations
  // TODO: Re-enable with proper drag detection after drag/drop is fixed
  // Reload features when window gains focus (to sync changes from other tabs/users)
  // useEffect(() => {
  //   const handleFocus = () => {
  //     loadFeatures();
  //   };

  //   window.addEventListener('focus', handleFocus);
  //   return () => window.removeEventListener('focus', handleFocus);
  // }, [loadFeatures]);

  return {
    features,
    isLoading,
    error,
    updateFeatures,
    reloadFeatures: loadFeatures
  };
}

/**
 * Hook for accessing a specific feature flag
 */
export function useFeatureFlag(flagName: keyof UiFeatureFlags): boolean {
  const { features } = useFeatureFlags();
  return features ? features[flagName] : false; // Default to false to prevent flash of content
}

/**
 * Hook for accessing a specific feature flag with loading state
 */
export function useFeatureFlagWithLoading(flagName: keyof UiFeatureFlags): { isEnabled: boolean; isLoading: boolean } {
  const { features, isLoading } = useFeatureFlags();
  return {
    isEnabled: features ? features[flagName] : false,
    isLoading
  };
}